import React, { useMemo } from 'react';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Legend,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { ChartColor, getChartGridColor } from '@t-one-internal/design-system';

// Support both snake_case (API) and camelCase formats
interface CompetencyData {
  competencyTitle?: string;
  competency_title?: string;
  expectedScore?: number;
  expected_score?: number;
  actualScore?: number;
  actual_score?: number;
  gap?: number;
  cycleName?: string;
  cycle_name?: string;
}

interface CycleOption {
  value: string;
  label: string;
}

interface CompetencyGapWidgetProps {
  data?: CompetencyData[] | { data: CompetencyData[] };
  loading?: boolean;
  error?: string;
  cycleOptions?: CycleOption[];
  selectedCycleId?: string;
  onCycleChange?: (cycleId: string) => void;
  cycleOptionsLoading?: boolean;
}

// Helpers for snake_case/camelCase compatibility
const getCompetencyTitle = (d: CompetencyData) =>
  d.competencyTitle || d.competency_title || 'Unknown';
const getExpectedScore = (d: CompetencyData) =>
  Number(d.expectedScore ?? d.expected_score ?? 0);
const getActualScore = (d: CompetencyData) =>
  Number(d.actualScore ?? d.actual_score ?? 0);


export const CompetencyGapWidget: React.FC<CompetencyGapWidgetProps> = ({
  data,
  loading,
  error,
  cycleOptions,
  selectedCycleId,
  onCycleChange,
  cycleOptionsLoading,
}) => {
  // Handle wrapped data format
  const competencies: CompetencyData[] = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if ((data as { data: CompetencyData[] })?.data) {
      return (data as { data: CompetencyData[] }).data;
    }
    return [];
  }, [data]);

  // Transform data for radar chart
  const chartData = useMemo(() => {
    const mapped = competencies.map((item) => ({
      competency: getCompetencyTitle(item),
      Expected: getExpectedScore(item),
      Actual: getActualScore(item),
      fullMark: 5,
    }));
    console.log('CompetencyGap chartData:', mapped);
    return mapped;
  }, [competencies]);



  if (loading) {
    return (
      <div className="p-5 h-full flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900 text-sm">Competency Gap Analysis</h3>
          {cycleOptions && cycleOptions.length > 0 && onCycleChange && (
            <select
              value={selectedCycleId || ''}
              onChange={(e) => onCycleChange(e.target.value)}
              disabled={cycleOptionsLoading}
              className="text-xs border border-gray-200 rounded-md px-2 py-1 text-gray-600 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-400 w-[200px] truncate"
            >
              {cycleOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          )}
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 h-full flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900 text-sm">Competency Gap Analysis</h3>
          {cycleOptions && cycleOptions.length > 0 && onCycleChange && (
            <select
              value={selectedCycleId || ''}
              onChange={(e) => onCycleChange(e.target.value)}
              disabled={cycleOptionsLoading}
              className="text-xs border border-gray-200 rounded-md px-2 py-1 text-gray-600 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-400 w-[200px] truncate"
            >
              {cycleOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          )}
        </div>
        <div className="flex-1 flex items-center justify-center text-red-500 text-sm">
          {error}
        </div>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="p-5 h-full flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900 text-sm">Competency Gap Analysis</h3>
          {cycleOptions && cycleOptions.length > 0 && onCycleChange && (
            <select
              value={selectedCycleId || ''}
              onChange={(e) => onCycleChange(e.target.value)}
              disabled={cycleOptionsLoading}
              className="text-xs border border-gray-200 rounded-md px-2 py-1 text-gray-600 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-400 w-[200px] truncate"
            >
              {cycleOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          )}
        </div>
        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
          No competency data available
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-gray-900 text-sm">Competency Gap Analysis</h3>
        {cycleOptions && cycleOptions.length > 0 && onCycleChange && (
          <select
            value={selectedCycleId || ''}
            onChange={(e) => onCycleChange(e.target.value)}
            disabled={cycleOptionsLoading}
            className="text-xs border border-gray-200 rounded-md px-2 py-1 text-gray-600 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-400 w-[200px] truncate"
          >
            {cycleOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        )}
      </div>

      {/* Chart: Bar for < 3 competencies, Radar for 3+ */}
      <div className="flex-1" style={{ minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          {chartData.length < 3 ? (
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
              maxBarSize={48}
            >
              <CartesianGrid stroke={getChartGridColor('grid')} strokeDasharray="3 3" horizontal={false} />
              <XAxis
                type="number"
                domain={[0, 5]}
                tick={{ fontSize: 10, fill: getChartGridColor('tickLabel') }}
                tickCount={6}
              />
              <YAxis
                type="category"
                dataKey="competency"
                tick={{ fontSize: 11, fill: '#374151' }}
                width={80}
                tickLine={false}
                axisLine={false}
              />
              <Bar dataKey="Expected" name="Expected" fill={ChartColor.indigo} fillOpacity={0.7} radius={[0, 4, 4, 0]} barSize={16} />
              <Bar dataKey="Actual" name="Actual" fill={ChartColor.amber} fillOpacity={0.7} radius={[0, 4, 4, 0]} barSize={16} />
              <Legend
                wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }}
                iconType="square"
                iconSize={10}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: getChartGridColor('tooltipBg'),
                  border: `1px solid ${getChartGridColor('tooltipBorder')}`,
                  borderRadius: '8px',
                  fontSize: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
                formatter={(value: number) => value.toFixed(2)}
              />
            </BarChart>
          ) : (
            <RadarChart cx="50%" cy="50%" outerRadius="70%" data={chartData}>
              <PolarGrid stroke={getChartGridColor('grid')} gridType="polygon" />
              <PolarAngleAxis
                dataKey="competency"
                tick={{ fontSize: 11, fill: '#374151' }}
                tickLine={false}
              />
              <PolarRadiusAxis
                angle={90}
                domain={[0, 5]}
                tick={{ fontSize: 10, fill: getChartGridColor('tickLabel') }}
                tickCount={6}
                axisLine={false}
              />
              <Radar
                name="Expected"
                dataKey="Expected"
                stroke={ChartColor.indigo}
                fill={ChartColor.indigo}
                fillOpacity={0.2}
                strokeWidth={2}
                dot={{ r: 4, fill: ChartColor.indigo, strokeWidth: 0 }}
              />
              <Radar
                name="Actual"
                dataKey="Actual"
                stroke={ChartColor.amber}
                fill={ChartColor.amber}
                fillOpacity={0.2}
                strokeWidth={2}
                dot={{ r: 4, fill: ChartColor.amber, strokeWidth: 0 }}
              />
              <Legend
                wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }}
                iconType="square"
                iconSize={10}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: getChartGridColor('tooltipBg'),
                  border: `1px solid ${getChartGridColor('tooltipBorder')}`,
                  borderRadius: '8px',
                  fontSize: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
                formatter={(value: number) => value.toFixed(2)}
              />
            </RadarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default CompetencyGapWidget;
